import { createAction, PrepareAction, SerializedError } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

type AsyncSagaPayloadCreator<Returned, SagaArg> = (arg: SagaArg) => Generator<any, Returned, any>;

export default function createAsyncSaga<Returned = void, SagaArg = void>(
    typePrefix: string,
    payloadCreator: AsyncSagaPayloadCreator<Returned, SagaArg>,
) {
    const request = createAction<PrepareAction<SagaArg>>(typePrefix, (arg: SagaArg) => ({
        payload: arg,
    }));
    type RequestAction = ReturnType<typeof request>;

    const pending = createAction(`${typePrefix}/pending`, (arg: SagaArg) => ({
        payload: undefined,
        meta: {
            arg,
        },
    }));
    const fulfilled = createAction<PrepareAction<Returned>>(`${typePrefix}/fulfilled`, (result: Returned) => ({
        payload: result,
    }));
    const rejected = createAction(`${typePrefix}/rejected`, (error: SerializedError | any, arg?: SagaArg) => ({
        payload: arg,
        error: error,
    }));

    const saga = function* (request: RequestAction) {
        const arg = request.payload;
        yield put(pending(arg));
        try {
            const result = yield* payloadCreator(arg);
            yield put(fulfilled(result));
        } catch (err) {
            yield put(rejected(err, arg));
        }
    };

    return Object.assign(request, {
        type: request.type,
        pending,
        fulfilled,
        rejected,
        saga,
    });
}
